import { useMutation, useQuery } from '@apollo/client'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { addJob } from '../../cache/jobs'
import apolloClient from '../../graphql/apolloClient'
import { GENERATE_MULTIPLE_RATE_PLAN_MUTATION } from '../../graphql/generateMultipleRateplan'
import { ROOMTYPES_QUERY } from '../../graphql/roomtype'
import { WALLET_TYPES_QUERY } from '../../graphql/walletTypes'
import { getTodayDate } from '../../helpers/date'
import { getCurrenciesAvailable } from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { JobsType } from '../../mockData/jobs'
import { Currency, SupportedWalletTypeInitValues } from '../../types/currency'
import {
  JobStatusForRateplan,
  RatePlanForGenerate,
  SupportedWalletType
} from '../../types/rateplan'
import { RoomTypeBrief } from '../../types/roomtype'
import { WalletType } from '../../types/wallet'
import { RateplanBasicDetailsForGenerate } from './RateplanBasicDetailsForGenerate'
import { RateplanFooter } from './RateplanFooter'
import { RateplanSupportedWalletTypes } from './RateplanSupportedWalletTypes'
import useStyles from './styles/RateplanMainForm.styles'
import { rateplanFormValidationSchema } from './validations/rateplanFormValidation'

const RATEPLAN_FORM_INIT_VALUES: RatePlanForGenerate = {
  basicDetails: {
    code: ['Standard'],
    name: '',
    nameTranslatedList: [
      {
        label: 'Iran, Islamic Republic of',
        nativeLabel: 'فارسی',
        value: ''
      }
    ],
    roomTypeCode: '',
    boardType: 'BREAKFAST',
    ratePolicy: '-',
    ratePolicyTranslatedList: [
      {
        label: 'Iran, Islamic Republic of',
        nativeLabel: 'فارسی',
        value: '-'
      }
    ],
    providerCode: '',
    type: 'Physical',
    parentRatePlanCode: ''
  },
  supportedWalletTypes: SupportedWalletTypeInitValues
}

const RateplanMainFormForGenerate = () => {
  const classes = useStyles()
  const history = useHistory()
  const params = useParams<{
    accommodationID: string
    propertyCode: string
    roomTypeID: string
  }>()
  const [roomTypeCode, setRoomTypeCode] = useState<string>()
  const [walletTypes, setWalletTypes] = useState<
    {
      currency: Currency
      walletType: string | null
    }[]
  >([])

  const [supportedWalletTypes, setSupportedWalletTypes] = useState<
    { label: string; value: string }[]
  >([])

  const { data: roomTypes, loading: loadingForRoomTypes } = useQuery<{
    roomTypes: RoomTypeBrief[]
  }>(ROOMTYPES_QUERY, {
    variables: {
      propertyCode: params.propertyCode,
      showDeletedRoomTypes: false,
      showDeactivedRoomTypes: false
    }
  })

  const { data: walletTypeList, loading: loadingWalletTypes } = useQuery<{
    walletTypes: WalletType[]
  }>(WALLET_TYPES_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const [mutateFunction, { loading: loadingForGenerateRatePlan }] = useMutation(
    GENERATE_MULTIPLE_RATE_PLAN_MUTATION
  )

  const handleSubmit = async (values: RatePlanForGenerate) => {
    const temp: SupportedWalletType[] = []
    values.supportedWalletTypes.forEach((item) => {
      if (item.isSelected) {
        temp.push({ ...item, isSelected: undefined })
      }
    })

    const updatedValues: RatePlanForGenerate = {
      ...values,
      supportedWalletTypes: temp
    }

    await mutateFunction({
      variables: {
        input: updatedValues,
        propertyCode: params.propertyCode,
        roomTypeCode: roomTypeCode
      }
    })
      .then(({ data: response }) => {
        apolloClient.cache.evict({
          fieldName: 'ratePlans'
        })

        const subjobs: JobStatusForRateplan[] = values.basicDetails.code.map(
          (item) => ({
            rateplanName: values.basicDetails.name,
            code: item,
            status: 'InProgress'
          })
        )

        const newJob: JobsType = {
          trackID: response.generateMultipleRatePlan.trackingID,
          creationDate: getTodayDate(),
          status: 'InProgress',
          hasDetails: true,
          forRatePlan: true,
          subjobsForGenerateRatePlan: subjobs
        }
        addJob(newJob)
        toast.info('Rateplan generation is in progress')
        history.push(
          `/${lang}/accommodations/${params.accommodationID}/${params.propertyCode}/rate-plans/list`
        )
      })
      .catch((item) => toast.error(item.message))
  }

  useEffect(() => {
    if (getCurrenciesAvailable()) {
      setWalletTypes([
        ...getCurrenciesAvailable().map((item) => {
          return { currency: item, walletType: null }
        })
      ])
    }
    if (walletTypeList) {
      setSupportedWalletTypes([
        ...walletTypeList.walletTypes.map((item: WalletType) => {
          return { label: item.walletType, value: item.walletType }
        })
      ])
    } else {
      setSupportedWalletTypes([
        { label: getTranslate('Nothing found'), value: '' }
      ])
    }
  }, [walletTypeList])

  return (
    <Formik
      enableReinitialize
      initialValues={RATEPLAN_FORM_INIT_VALUES}
      onSubmit={handleSubmit}
      validationSchema={rateplanFormValidationSchema}>
      {({
        values,
        setFieldValue,
        errors,
        touched,
        handleBlur,
        isSubmitting
      }) => {
        const isPhysical = values.basicDetails.type === 'Physical'
        return (
          <Form>
            <RateplanBasicDetailsForGenerate
              {...{
                values,
                setFieldValue,
                errors,
                touched,
                handleBlur,
                isSubmitting,
                roomTypes: roomTypes ? roomTypes.roomTypes : [],
                setRoomTypeCode,
                isPhysical,
                propertyCode: params.propertyCode,
                loadingForRoomTypes,
                classes
              }}
            />
            {isPhysical && (
              <RateplanSupportedWalletTypes
                {...{
                  values,
                  setFieldValue,
                  errors,
                  touched,
                  handleBlur,
                  isSubmitting,
                  options: walletTypes,
                  supportedWalletTypes,
                  isPhysical,
                  classes
                }}
                loading={loadingWalletTypes}
              />
            )}
            <RateplanFooter
              isSubmitting={isSubmitting}
              loading={loadingForGenerateRatePlan}
            />
          </Form>
        )
      }}
    </Formik>
  )
}

export default RateplanMainFormForGenerate
